import { FinalizationRepository } from "../FinalizationRepository";
import {
  FinalizationItemModel,
  PageableModel,
  GetListFinalizationParams,
  SettlementDetailModel,
  ExportMerchantSettlementParams,
  FinalizationStatus,
} from "@/models/FinalizationModel";
import { ErrorModel } from "@/models/ErrorModel";
import { KienlongPayClient } from "@/api/kienlong_pay_http_client";
import {
  mapFinalizationListResponse,
  mapFinalizationDetailModel,
} from "@/adapters/finalization_adapter";

/**
 * Implementation của FinalizationRepository sử dụng SettlementControllerApi
 *
 * API endpoints:
 * - GET /api/settlements/portal/v1/get-list (Merchant)
 * - GET /api/settlements/portal/v1/get-detail/{id} (Merchant)
 */
export class FinalizationRepositoryImp implements FinalizationRepository {
  private client: KienlongPayClient;

  constructor(kienlongPayClient: KienlongPayClient) {
    this.client = kienlongPayClient;
  }

  /**
   * GET /api/settlements/portal/v1/get-list
   */
  public async getListFinalization(
    params: GetListFinalizationParams,
  ): Promise<PageableModel<FinalizationItemModel>> {
    try {
      const resp =
        await this.client.settlementController.getSettlementByMerchant({
          textSearch: params.keyword,
          fromDate: params.fromDate,
          toDate: params.toDate,
          status:
            params.statuses && params.statuses.length > 0
              ? (params.statuses[0] as "SUCCESS" | "PENDING" | "CANCELLED")
              : undefined,
          page: params.page,
          size: params.size,
        });

      if (resp.data.code === 0 && resp.data.data) {
        return mapFinalizationListResponse(resp.data.data);
      } else {
        throw new ErrorModel({
          code: resp.data.code || 500,
          message: resp.data.message || "Lỗi khi lấy danh sách kết toán",
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/settlements/portal/v1/get-detail/{id}
   */
  public async getFinalizationDetail(params: {
    id: string;
    page?: number;
    size?: number;
    sort?: string[];
  }): Promise<SettlementDetailModel> {
    try {
      const resp =
        await this.client.settlementController.getSettlementByMerchant1({
          settlementId: params.id,
          page: params.page,
          size: params.size,
          sort: params.sort,
        });

      if (resp.data.code === 0 && resp.data.data) {
        return mapFinalizationDetailModel(resp.data.data);
      } else {
        throw new ErrorModel({
          code: resp.data.code || 404,
          message:
            resp.data.message ||
            `Không tìm thấy phiên kết toán với ID: ${params.id}`,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/settlements/portal/v1/export/{id}
   */
  public async exportSettlement(
    params: ExportMerchantSettlementParams,
  ): Promise<Blob> {
    try {
      const resp = await this.client.settlementController.exportSettlements(
        {
          ...params,
          status: params.status
            ? (params.status as "SUCCESS" | "PENDING" | "CANCELLED")
            : undefined,
        },
        {
          responseType: "blob",
        },
      );

      if (resp.data) {
        return resp.data;
      } else {
        throw new ErrorModel({
          code: 500,
          message: "Không thể export file kết toán",
        });
      }
    } catch (error) {
      throw error;
    }
  }
}
